import {
 forwardRef,
 useId,
 useMemo,
 useState,
 type HTMLAttributes,
} from "react";
import { Button } from "./button.js";
import { Input } from "./input.js";
import { ToolCard } from "./tool-card.js";
import { cn } from "./lib/cn.js";
import {
 TOOL_NAMES_ORDERED,
 ToolKind,
 getToolSpec,
 toolKindLabel,
 type MountClass,
 type ToolSpec,
} from "./lib/tool-catalog.js";

/**
 * Filterable browser of every tool in the v2 catalog. Used as the
 * left-hand picker pane of the Mech Creator — paired with
 * `<LoadoutBuilder>` (FE-MC2) which receives the click via
 * `onSelect`.
 *
 * Filters in order of UX prominence:
 *
 * 1. Kind chips (Movement / Direct-fire / Missile / Melee /
 * Reactive / Passive / Sensor / Heat utility) — toggle pins
 * the visible set; clicking the active one returns to "All".
 * 2. Mount filter (Arm / Torso / None) — single dropdown-style
 * chip row.
 * 3. Name search — case-insensitive substring over the tool name.
 *
 * Selection model is intentionally not built into this component:
 * the caller owns it via `selected` (highlighted state) and `exclude`
 * (filtered out entirely). This keeps the catalog suitable for both
 * "drop into LoadoutBuilder" flows (where already-picked tools should
 * disappear) and "browse the v2 catalog" flows (where everything
 * stays visible).
 */
export interface ToolCatalogGridProps
 extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
 /** Fires when the user clicks a tool. */
 onSelect?: (toolName: string) => void;
 /** Names of tools to hide entirely (e.g. already in the loadout). */
 exclude?: readonly string[];
 /** Names of tools to render with a "selected" highlight ring. */
 selected?: readonly string[];
 /** Initial active kind filter (null = All). Uncontrolled. */
 initialKindFilter?: ToolKind | null;
 /** Initial active mount filter (null = Any). Uncontrolled. */
 initialMountFilter?: MountClass | null;
 /** Heading text. Set to `""` to hide the heading. */
 heading?: string;
}

const KIND_FILTERS: readonly ToolKind[] = [
 ToolKind.MOVEMENT,
 ToolKind.DIRECT_FIRE,
 ToolKind.MISSILE,
 ToolKind.MELEE,
 ToolKind.REACTIVE,
 ToolKind.PASSIVE,
 ToolKind.SENSOR,
 ToolKind.HEAT_UTILITY,
];

const MOUNT_FILTERS: readonly MountClass[] = ["ARM", "TORSO", "NONE"];

function mountLabel(mount: MountClass): string {
 switch (mount) {
 case "ARM": return "Arm";
 case "TORSO": return "Torso";
 case "NONE": return "Body";
 }
}

export const ToolCatalogGrid = forwardRef<HTMLDivElement, ToolCatalogGridProps>(
 (
 {
 onSelect,
 exclude,
 selected,
 initialKindFilter = null,
 initialMountFilter = null,
 heading = "Tool catalog",
 className,
 ...rest
 },
 ref,
 ) => {
 const searchId = useId();
 const [kindFilter, setKindFilter] = useState<ToolKind | null>(
 initialKindFilter,
 );
 const [mountFilter, setMountFilter] = useState<MountClass | null>(
 initialMountFilter,
 );
 const [search, setSearch] = useState("");

 const excludeSet = useMemo(
 () => new Set(exclude ?? []),
 [exclude],
 );
 const selectedSet = useMemo(
 () => new Set(selected ?? []),
 [selected],
 );

 const visible = useMemo<ToolSpec[]>(() => {
 const needle = search.trim().toUpperCase();
 return TOOL_NAMES_ORDERED.flatMap((name) => {
 if (excludeSet.has(name)) return [];
 const spec = getToolSpec(name);
 if (!spec) return [];
 if (kindFilter !== null && spec.kind !== kindFilter) return [];
 if (mountFilter !== null && spec.mount !== mountFilter) return [];
 if (needle && !spec.name.includes(needle)) return [];
 return [spec];
 });
 }, [excludeSet, kindFilter, mountFilter, search]);

 const totalVisible = visible.length;
 const totalAvailable =
 TOOL_NAMES_ORDERED.length - excludeSet.size;

 return (
 <div
 ref={ref}
 className={cn("flex flex-col gap-3", className)}
 {...rest}
 >
 {heading && (
 <header className="flex items-baseline justify-between gap-3">
 <h3 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 {heading}
 </h3>
 <span className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] tabular-nums text-[var(--pp-text-muted)]">
 {totalVisible} / {totalAvailable}
 </span>
 </header>
 )}

 <fieldset className="flex flex-wrap gap-1.5" aria-label="Filter by kind">
 <FilterChip
 active={kindFilter === null}
 onClick={() => setKindFilter(null)}
 >
 All
 </FilterChip>
 {KIND_FILTERS.map((kind) => (
 <FilterChip
 key={kind}
 active={kindFilter === kind}
 onClick={() =>
 setKindFilter(kindFilter === kind ? null : kind)
 }
 >
 {toolKindLabel(kind)}
 </FilterChip>
 ))}
 </fieldset>

 <fieldset
 className="flex flex-wrap items-center gap-1.5"
 aria-label="Filter by mount"
 >
 <span className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 Mount:
 </span>
 <FilterChip
 active={mountFilter === null}
 onClick={() => setMountFilter(null)}
 >
 Any
 </FilterChip>
 {MOUNT_FILTERS.map((mount) => (
 <FilterChip
 key={mount}
 active={mountFilter === mount}
 onClick={() =>
 setMountFilter(mountFilter === mount ? null : mount)
 }
 >
 {mountLabel(mount)}
 </FilterChip>
 ))}
 </fieldset>

 <div className="flex flex-col gap-1">
 <label
 htmlFor={searchId}
 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]"
 >
 Search by name
 </label>
 <Input
 id={searchId}
 value={search}
 onChange={(e) => setSearch(e.currentTarget.value)}
 placeholder="GAUSS, SRM, JUMP…"
 spellCheck={false}
 autoComplete="off"
 />
 </div>

 {totalVisible === 0 ? (
 <div
 role="status"
 className=" border border-dashed border-[var(--pp-border)] p-6 text-center text-[var(--pp-font-md)] text-[var(--pp-text-muted)]"
 >
 No tools match these filters.
 </div>
 ) : (
 <ul
 className="flex flex-wrap gap-2"
 aria-label="Tools matching the active filters"
 >
 {visible.map((spec) => {
 const isSelected = selectedSet.has(spec.name);
 return (
 <li key={spec.name}>
 <button
 type="button"
 onClick={() => onSelect?.(spec.name)}
 className={cn(
 " text-left transition-[box-shadow,outline]",
 "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)]",
 isSelected &&
 "ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--background)]",
 )}
 aria-pressed={isSelected || undefined}
 aria-label={`Add ${spec.name}`}
 >
 <ToolCard tool={spec} />
 </button>
 </li>
 );
 })}
 </ul>
 )}
 </div>
 );
 },
);
ToolCatalogGrid.displayName = "ToolCatalogGrid";

function FilterChip({
 active,
 onClick,
 children,
}: {
 active: boolean;
 onClick: () => void;
 children: React.ReactNode;
}) {
 // Chip itself is `<span>` — we want a clickable affordance, so wrap
 // a <Button variant="ghost"> in chip styling. This keeps focus +
 // keyboard activation correct without re-implementing them.
 return (
 <Button
 type="button"
 variant="ghost"
 size="sm"
 onClick={onClick}
 className={cn(
 "h-auto px-2 py-0.5 border",
 "font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] whitespace-nowrap",
 active
 ? "border-[var(--accent)] text-[var(--accent)]"
 : "border-[var(--pp-border)] text-[var(--pp-text-muted)] hover:text-[var(--pp-text)]",
 )}
 aria-pressed={active}
 >
 {active && <span aria-hidden>● </span>}
 {children}
 </Button>
 );
}

// Helper: render a Chip-flavored summary of which filters are active.
// Used by Mech Creator's filter status row in a later PR; exported for
// reuse but not a required entry point.
export function describeActiveFilters(args: {
 kind: ToolKind | null;
 mount: MountClass | null;
 search: string;
}): string {
 const parts: string[] = [];
 if (args.kind !== null) parts.push(toolKindLabel(args.kind));
 if (args.mount !== null) parts.push(`${mountLabel(args.mount)}-mounted`);
 if (args.search.trim()) parts.push(`"${args.search.trim()}"`);
 return parts.length > 0 ? parts.join(" · ") : "All tools";
}

/* Re-export aliased name — short form for ergonomic imports. The
 primary type lives on `ToolCatalogGrid` to keep it distinct from
 the data structure `TOOL_CATALOG` exported from `./lib/tool-catalog`. */
export const ToolCatalog = ToolCatalogGrid;
